import type { Activity, SessionChunk } from 'features/script/script-model'

export type AgendaPosition = 'done' | 'current' | 'upcoming'

/**
 * Consecutive activities of one type, shown once: a deck of six slides is
 * "Slide ×6", and "3 / 6" while its third slide is on.
 */
export type ActivityGroup = {
	activity: Activity
	count: number
	position: AgendaPosition
	/** 1-based, only in the group that holds the current activity. */
	currentNumber?: number
}

export type AgendaRow = {
	key: string
	chunkIndex: number
	title: string
	plannedStart: Date
	position: AgendaPosition
	groups: ActivityGroup[]
}

export type ScriptPositionState = {
	chunks: SessionChunk[]
	currentSessionChunkIndex: number
	currentItemIndex: number
	currentActivityIndex: number
}

const compare = (a: [number, number], b: [number, number]): number =>
	a[0] - b[0] || a[1] - b[1]

const groupActivities = (
	activities: Activity[],
	positionOf: (index: number) => AgendaPosition
): ActivityGroup[] => {
	const groups: ActivityGroup[] = []
	activities.forEach((activity, index) => {
		const position = positionOf(index)
		const last = groups[groups.length - 1]
		const inGroup = last && last.activity.type === activity.type
		const group: ActivityGroup = inGroup
			? last
			: { activity, count: 0, position }
		group.count += 1
		if (position === 'current') {
			group.position = 'current'
			group.currentNumber = group.count
		} else if (group.position !== 'current' && position === 'upcoming') {
			group.position = 'upcoming'
		}
		if (!inGroup) {
			groups.push(group)
		}
	})
	return groups
}

/**
 * The whole script as the presenter panel lists it: every item of every
 * chunk, each done, current or upcoming against the script's position. Before
 * the script starts nothing is current and everything is upcoming.
 */
export const agendaOutline = ({
	chunks,
	currentSessionChunkIndex,
	currentItemIndex,
	currentActivityIndex,
}: ScriptPositionState): AgendaRow[] => {
	const started = !!chunks[currentSessionChunkIndex]?.items[currentItemIndex]
	const current: [number, number] = [currentSessionChunkIndex, currentItemIndex]

	return chunks.flatMap((chunk, chunkIndex) =>
		chunk.items.map((item, itemIndex) => {
			const order = started ? compare([chunkIndex, itemIndex], current) : 1
			const position: AgendaPosition =
				order < 0 ? 'done' : order === 0 ? 'current' : 'upcoming'
			return {
				key: `${chunkIndex}/${itemIndex}`,
				chunkIndex,
				title: item.title,
				plannedStart: item.startTime,
				position,
				groups: groupActivities(item.activities, index =>
					position !== 'current'
						? position
						: index < currentActivityIndex
						? 'done'
						: index === currentActivityIndex
						? 'current'
						: 'upcoming'
				),
			}
		})
	)
}
